import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

interface AnalyticsTrackerProps {
  googleAnalyticsId?: string;
  facebookPixelId?: string;
}

export const AnalyticsTracker = ({ 
  googleAnalyticsId = 'G-XXXXXXXXXX', // Substituir pela ID real
  facebookPixelId = '000000000000000' // Substituir pela ID real
}: AnalyticsTrackerProps) => {
  const location = useLocation();

  // Inicializar Google Analytics
  useEffect(() => {
    if (!googleAnalyticsId || googleAnalyticsId === 'G-XXXXXXXXXX') return;

    // Carregar script do GA4
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
    document.head.appendChild(script);

    // Inicializar dataLayer
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer?.push(args);
    }
    window.gtag = gtag;
    window.gtag('js', new Date());
    window.gtag('config', googleAnalyticsId, {
      page_path: location.pathname + location.search,
      send_page_view: true
    });

    console.log('Google Analytics inicializado:', googleAnalyticsId);
  }, [googleAnalyticsId]);

  // Inicializar Facebook Pixel
  useEffect(() => {
    if (!facebookPixelId || facebookPixelId === '000000000000000') return;

    // Carregar Facebook Pixel
    const loadFacebookPixel = (f: any, b: any, e: any, v: any) => {
      if (f.fbq) return;
      const n: any = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = b.createElement(e);
      t.async = true;
      t.src = v;
      const s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    };
    
    loadFacebookPixel(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq?.('init', facebookPixelId);
    window.fbq?.('track', 'PageView');

    console.log('Facebook Pixel inicializado:', facebookPixelId);
  }, [facebookPixelId]);

  // Rastrear mudanças de página
  useEffect(() => {
    // Google Analytics
    if (window.gtag && googleAnalyticsId !== 'G-XXXXXXXXXX') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }

    // Facebook Pixel
    if (window.fbq && facebookPixelId !== '000000000000000') {
      window.fbq('track', 'PageView');
    }
  }, [location, googleAnalyticsId, facebookPixelId]);

  return null;
};

// Funções auxiliares para rastreamento de eventos
export const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }

  // Facebook Pixel
  if (window.fbq) {
    window.fbq('trackCustom', eventName, eventParams);
  }

  console.log('Event tracked:', eventName, eventParams);
};

export const trackConversion = (type: 'signup' | 'purchase' | 'subscribe', value?: number, currency = 'BRL') => {
  const eventMap = {
    signup: 'CompleteRegistration',
    purchase: 'Purchase',
    subscribe: 'Subscribe'
  };

  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-CONVERSION_ID', // Substituir
      value: value || 0,
      currency: currency,
      transaction_id: Date.now().toString()
    });
  }

  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', eventMap[type], {
      value: value || 0,
      currency: currency
    });
  }

  console.log('Conversion tracked:', type, value);
};

export const trackLead = (data?: { email?: string; name?: string; phone?: string }) => {
  // Facebook Pixel Lead
  if (window.fbq) {
    window.fbq('track', 'Lead', data);
  }

  // Google Analytics Lead
  if (window.gtag) {
    window.gtag('event', 'generate_lead', {
      value: 1,
      ...data
    });
  }

  console.log('Lead tracked:', data);
};
